import { createAction, createReducer, Dispatch } from '@reduxjs/toolkit';
import { FileUploadSetting } from 'backend';
import { useDispatch, useSelector } from 'react-redux';
import { FileUploadThumbnail } from 'shared';
import { updatePage } from '../commons';
import { RootState } from '../store';

export const FILE_UPLOAD_SESSION_STATE_TYPE = {
  notReady: 'notReady',
  ready: 'ready',
  error: 'error',
} as const;

export type FileUploadSessionStateNotReady = {
  type: typeof FILE_UPLOAD_SESSION_STATE_TYPE.notReady;
};

export type FileUploadSessionStateReady = {
  type: typeof FILE_UPLOAD_SESSION_STATE_TYPE.ready;
  submissionSession: {
    submissionId: string;
    expiration: string;
    accessKeyId: string;
    secretAccessKey: string;
    token: string;
    mediaFileStore: {
      uploadPathMap: {
        [key: string]: string;
      };
      propertyMap: {
        bucketName: string;
        region: string;
      };
    };
  };
};

export type FileUploadSessionStateError = {
  type: typeof FILE_UPLOAD_SESSION_STATE_TYPE.error;
  errorMessage: string;
};

export type FileUploadSessionState =
  | FileUploadSessionStateNotReady
  | FileUploadSessionStateReady
  | FileUploadSessionStateError;

export type FileUploadItemState = {
  awaiting: boolean;
  needsToReload: boolean;
  thumbnail?: FileUploadThumbnail;
};

type FileUploadItems = {
  [subjectId: string]: FileUploadItemState;
};

export type FileUploadState = {
  session: FileUploadSessionState;
  setting: FileUploadSetting | undefined;
  items: FileUploadItems;
};

// actions
const UPDATE_FILE_UPLOAD_SESSION_STATE =
  'fileUpload/updateFileUploadSessionState';

export const updateFileUploadSessionState = createAction<
  FileUploadSessionState,
  typeof UPDATE_FILE_UPLOAD_SESSION_STATE
>(UPDATE_FILE_UPLOAD_SESSION_STATE);

export type UpdateFileUploadSessionStateAction = ReturnType<
  typeof updateFileUploadSessionState
>;

const UPDATE_FILE_UPLOAD_ITEM_STATE = 'fileUpload/updateFileUploadItemState';

export type UpdateFileUploadItemStatePayload = Partial<FileUploadItemState> & {
  subjectId: string;
};

export const updateFileUploadItemState = createAction<
  UpdateFileUploadItemStatePayload,
  typeof UPDATE_FILE_UPLOAD_ITEM_STATE
>(UPDATE_FILE_UPLOAD_ITEM_STATE);

export type UpdateFileUploadItemStateAction = ReturnType<
  typeof updateFileUploadItemState
>;

// dispatch
export const useUpdateFileUploadSessionStateDispatch =
  (): Dispatch<UpdateFileUploadSessionStateAction> =>
    useDispatch<Dispatch<UpdateFileUploadSessionStateAction>>();
export const useUpdateFileUploadItemStateDispatch =
  (): Dispatch<UpdateFileUploadItemStateAction> =>
    useDispatch<Dispatch<UpdateFileUploadItemStateAction>>();

// reducer
const fileUploadInitialState: FileUploadState = {
  session: {
    type: FILE_UPLOAD_SESSION_STATE_TYPE.notReady,
  },
  setting: undefined,
  items: {},
};

export const fileUploadReducer = createReducer<FileUploadState>(
  fileUploadInitialState,
  (builder) => {
    builder.addCase(updateFileUploadSessionState, (state, { payload }) => {
      if (payload.type === FILE_UPLOAD_SESSION_STATE_TYPE.ready) {
        const items = Object.keys(
          payload.submissionSession.mediaFileStore.uploadPathMap,
        ).reduce<FileUploadItems>((acc, subjectId) => {
          return {
            ...acc,
            [subjectId]: {
              needsToReload: false,
              awaiting: false,
            },
          };
        }, {});
        return {
          ...state,
          session: payload,
          items: {
            ...items,
            ...state.items,
          },
        };
      }
      return {
        ...state,
        session: payload,
      };
    });
    builder.addCase(updatePage, (state, action) => {
      const { fileUploadSetting } = action.payload;
      return {
        ...state,
        setting: fileUploadSetting,
      };
    });
    builder.addCase(updateFileUploadItemState, (state, action) => {
      const { subjectId, ...updatedItemsState } = action.payload;
      return {
        ...state,
        items: {
          ...state.items,
          [subjectId]: {
            ...state.items[subjectId],
            ...updatedItemsState,
          },
        },
      };
    });
  },
);

// selector
export const useFileUpload = (): FileUploadState =>
  useSelector<RootState, FileUploadState>((rootState) => rootState.fileUpload);

export const useFileUploadItem = (
  subjectId: string,
): FileUploadItemState | undefined =>
  useSelector<RootState, FileUploadItemState | undefined>(
    (rootState) => rootState.fileUpload.items[subjectId],
  );
